/**
 * Teach by recording (bots B5 half 2, 2026-09-07) — the picker's second
 * half. The user names a site, an agent and a routine, a HEADED window
 * opens on their own desktop (the agent cannot see it — it is not a
 * screen), they do the task once and close the window or press Stop; the
 * steps become a routine DRAFT the Routines page (and the chat card) can
 * try, edit, schedule and save. A password typed in that window is never
 * recorded; the server says so and this panel shows the steps as they
 * arrive so the user can check.
 */
import { Circle, Square, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
	cancelRecording,
	fetchAgentRoster,
	fetchRecording,
	fetchRecordings,
	startRecording,
	stopRecording,
	type Recording,
	type RosterAgent,
} from "../../../../lib/api";
import { toast } from "../../../../stores/toast";
import { Button } from "../../../common/Button";
import styles from "./BrowserPanel.module.css";

const OPEN = new Set(["starting", "recording"]);

export function RecordSection() {
	const { t } = useTranslation();
	const [roster, setRoster] = useState<RosterAgent[]>([]);
	const [host, setHost] = useState("");
	const [agent, setAgent] = useState("main");
	const [name, setName] = useState("");
	const [rec, setRec] = useState<Recording | null>(null);
	const [draft, setDraft] = useState<{ id: string; name: string } | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let alive = true;
		fetchAgentRoster()
			.then((r) => { if (alive) setRoster(r.filter((a) => a.status === "active")); })
			.catch(() => { if (alive) setRoster([]); });
		// A recording that is still open when the panel is reopened stays visible.
		fetchRecordings()
			.then((rows) => { if (!alive) return; const open = rows.find((r) => OPEN.has(r.status)); if (open) setRec(open); })
			.catch(() => { /* no recording to resume */ });
		return () => { alive = false; };
	}, []);

	const open = !!rec && OPEN.has(rec.status);
	useEffect(() => {
		if (!rec || !OPEN.has(rec.status)) return;
		const id = rec.id;
		const timer = setInterval(() => {
			fetchRecording(id).then(setRec).catch(() => { /* keep the last state */ });
		}, 1500);
		return () => clearInterval(timer);
	}, [rec?.id, rec?.status]); // eslint-disable-line react-hooks/exhaustive-deps

	const start = async () => {
		if (busy) return;
		setBusy(true);
		try {
			setDraft(null);
			setRec(await startRecording({ host: host.trim(), agent_key: agent, name: name.trim() }));
		} catch (e) {
			toast.error(t("dock.browser.record.startFailed", "Could not start the recording"), String((e as Error).message ?? e));
		} finally {
			setBusy(false);
		}
	};

	const stop = async () => {
		if (!rec || busy) return;
		setBusy(true);
		try {
			const res = await stopRecording(rec.id);
			setRec(res.recording);
			if (res.draft) {
				setDraft({ id: res.draft.id, name: res.draft.name });
				toast.success(t("dock.browser.record.saved", { name: res.draft.name, defaultValue: "Draft ready: {{name}}" }), t("dock.browser.record.savedHint", "Review it under Routines: try it, set a schedule, save."));
			} else {
				toast.info(t("dock.browser.record.title", "Teach by recording"), t("dock.browser.record.noSteps", "Nothing was recorded, so no draft was created."));
			}
		} catch (e) {
			toast.error(t("dock.browser.record.stopFailed", "Could not stop the recording"), String((e as Error).message ?? e));
		} finally {
			setBusy(false);
		}
	};

	const cancel = async () => {
		if (!rec || busy) return;
		setBusy(true);
		try {
			await cancelRecording(rec.id);
			setRec(null);
		} catch (e) {
			toast.error(t("dock.browser.record.stopFailed", "Could not stop the recording"), String((e as Error).message ?? e));
		} finally {
			setBusy(false);
		}
	};

	const reset = () => { setRec(null); setDraft(null); setName(""); };

	return (
		<section className={styles.record} data-record-section aria-label={t("dock.browser.record.title", "Teach by recording")}>
			<div className={styles.recordHead}>
				<Circle size={12} aria-hidden className={open ? styles.recordDotLive : styles.recordDot} />
				<span className={styles.title}>{t("dock.browser.record.title", "Teach by recording")}</span>
			</div>
			{draft ? (
				<div className={styles.recordDone} data-record-draft={draft.id}>
					<span>{t("dock.browser.record.saved", { name: draft.name, defaultValue: "Draft ready: {{name}}" })}</span>
					<Link to="/routines" className={styles.recordLink}>{t("dock.browser.record.openRoutines", "Open Routines")}</Link>
					<Button variant="ghost" size="sm" onClick={reset}>{t("dock.browser.record.another", "Record another")}</Button>
				</div>
			) : rec ? (
				<div className={styles.recordLive} data-recording={rec.id} data-recording-status={rec.status}>
					<div className={styles.recordMeta}>
						<span className={styles.name}>{rec.name}</span>
						<span className={styles.meta}>{rec.host} · {rec.agent_key} · {t("dock.browser.record.steps", { count: rec.step_count, defaultValue: "{{count}} step(s)" })}{rec.truncated ? " · " + t("dock.browser.record.truncated", "truncated") : ""}</span>
					</div>
					{rec.error && <div className={styles.error}>{rec.error}</div>}
					{rec.steps.length > 0 && (
						<ol className={styles.recordSteps} data-record-steps>
							{rec.steps.slice(-4).map((s, i) => <li key={`${rec.steps.length - 4 + i}-${s}`}>{s}</li>)}
						</ol>
					)}
					<div className={styles.recordActions}>
						{open ? (
							<>
								<span className={styles.muted}>{t("dock.browser.record.recording", "Recording — do the task in the window, then stop here or close it.")}</span>
								<Button variant="primary" size="sm" icon={<Square size={12} />} loading={busy} onClick={() => void stop()}>{t("dock.browser.record.stop", "Stop & save draft")}</Button>
								<Button variant="ghost" size="sm" icon={<X size={12} />} disabled={busy} onClick={() => void cancel()}>{t("dock.browser.record.cancel", "Cancel")}</Button>
							</>
						) : rec.status === "cancelled" ? (
							<Button variant="ghost" size="sm" onClick={reset}>{t("dock.browser.record.another", "Record another")}</Button>
						) : (
							<>
								<span className={styles.muted}>{t("dock.browser.record.stopped", "The window closed — save what was recorded as a draft.")}</span>
								<Button variant="primary" size="sm" loading={busy} onClick={() => void stop()}>{t("dock.browser.record.saveDraft", "Save as draft")}</Button>
								<Button variant="ghost" size="sm" disabled={busy} onClick={reset}>{t("dock.browser.record.discard", "Discard")}</Button>
							</>
						)}
					</div>
				</div>
			) : (
				<form className={styles.recordForm} onSubmit={(ev) => { ev.preventDefault(); void start(); }}>
					<p className={styles.muted}>{t("dock.browser.record.hint", "Do a task once in a window the agent cannot see; what you do becomes a routine draft for that agent. A password you type is never recorded.")}</p>
					<input className={styles.urlInput} value={host} onChange={(e) => setHost(e.target.value)} placeholder={t("dock.browser.record.hostPlaceholder", "example.com")} aria-label={t("dock.browser.record.host", "Site")} required disabled={busy} />
					<select className={styles.urlInput} value={agent} onChange={(e) => setAgent(e.target.value)} aria-label={t("dock.browser.record.agent", "Agent")} disabled={busy}>
						{!roster.some((a) => a.key === "main") && <option value="main">main</option>}
						{roster.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
					</select>
					<input className={styles.urlInput} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("dock.browser.record.namePlaceholder", "Weekly export")} aria-label={t("dock.browser.record.name", "Routine name")} required disabled={busy} />
					<Button type="submit" size="sm" variant="secondary" icon={<Circle size={12} />} loading={busy} disabled={!host.trim() || !name.trim()}>
						{t("dock.browser.record.start", "Start recording")}
					</Button>
				</form>
			)}
		</section>
	);
}
